import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Sparkles, Calendar, Camera, ShoppingBag, Layers,
  Megaphone, Share2, Palette, Clapperboard, Globe, Users, TrendingUp, Package, Star,
  ClipboardList, Lightbulb, Rocket, BarChart3, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

const ICONS = { Megaphone, Share2, Palette, Clapperboard, Camera, Package, Globe, Users, TrendingUp };

const CARD_THEMES = [
  { bg: "bg-gradient-to-br from-pink-50 to-rose-100",   border: "border-pink-200",   iconBg: "bg-pink-100",   iconColor: "text-[#FF007F]",  dot: "bg-[#FF007F]",  link: "text-[#FF007F]" },
  { bg: "bg-gradient-to-br from-orange-50 to-amber-100", border: "border-orange-200", iconBg: "bg-orange-100", iconColor: "text-[#FF7F00]",  dot: "bg-[#FF7F00]",  link: "text-[#FF7F00]" },
  { bg: "bg-gradient-to-br from-cyan-50 to-sky-100",     border: "border-cyan-200",   iconBg: "bg-cyan-100",   iconColor: "text-[#00BFE0]",  dot: "bg-[#00BFE0]",  link: "text-[#00BFE0]" },
  { bg: "bg-gradient-to-br from-violet-50 to-purple-100",border: "border-violet-200", iconBg: "bg-violet-100", iconColor: "text-[#9B00FF]",  dot: "bg-[#9B00FF]",  link: "text-[#9B00FF]" },
  { bg: "bg-gradient-to-br from-emerald-50 to-green-100",border: "border-emerald-200",iconBg: "bg-emerald-100",iconColor: "text-emerald-600",dot: "bg-emerald-500",link: "text-emerald-600" },
  { bg: "bg-gradient-to-br from-blue-50 to-indigo-100",  border: "border-blue-200",   iconBg: "bg-blue-100",   iconColor: "text-blue-600",   dot: "bg-blue-500",   link: "text-blue-600" },
];

function CountUp({ end, suffix = "", decimals = 0, duration = 2000, delay = 0 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px -60px 0px" });
  const started = useRef(false);

  useEffect(() => {
    if (!isInView || started.current) return;
    started.current = true;
    const timeout = setTimeout(() => {
      const startTime = performance.now();
      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = eased * end;
        setCount(decimals > 0 ? parseFloat(current.toFixed(decimals)) : Math.floor(current));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, delay);
    return () => clearTimeout(timeout);
  }, [isInView, end, duration, decimals, delay]);

  return (
    <span ref={ref}>
      {decimals > 0 ? count.toFixed(decimals) : count}{suffix}
    </span>
  );
}

export default function Home() {
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [cases, setCases] = useState([]);
  const [tess, setTess] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/portfolio").then((r) => setPortfolio(r.data.slice(0, 6)));
    api.get("/case-studies").then((r) => setCases(r.data));
    api.get("/testimonials").then((r) => setTess(r.data.slice(0, 4)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-28 pb-24 lg:pt-36 lg:pb-32 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-[28rem] h-[28rem] rounded-full bg-[#FF007F]/15 blur-3xl animate-blob" />
        <div className="absolute top-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#00BFE0]/15 blur-3xl animate-blob" style={{ animationDelay: "2s" }} />
        <div className="absolute bottom-0 left-1/3 w-80 h-80 rounded-full bg-[#FF7F00]/15 blur-3xl animate-blob" style={{ animationDelay: "4s" }} />

        <div className="container mx-auto px-4 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-zinc-100 border border-zinc-200 text-xs font-mono mb-6">
                <Sparkles className="w-3.5 h-3.5 text-[#FF007F]" />
                <span>A creative & digital growth partner</span>
              </div>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-[#0A1128] mb-6">
                Your <span className="text-gradient-animate">Brand</span>.<br />
                Our <span className="text-gradient-animate">Strategy</span>.
              </h1>
              <p className="text-base sm:text-lg text-[#52525B] max-w-xl mb-8 leading-relaxed">
                Branding • Advertising • Production • Studio Rentals • Digital Growth.
                Everything your brand needs to stand out — under one roof.
              </p>

              <div className="flex flex-wrap gap-3">
                <Link to="/contact" data-testid="hero-cta-consultation"
                  className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0A1128] text-white font-semibold hover:bg-gradient-brand transition">
                  <Calendar className="w-4 h-4" /> Book a Consultation
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                </Link>
                <Link to="/services" data-testid="hero-cta-services"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#0A1128] text-[#0A1128] font-semibold hover:bg-[#0A1128] hover:text-white transition">
                  <Layers className="w-4 h-4" /> Explore Services
                </Link>
                <Link to="/studios" data-testid="hero-cta-studio"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-zinc-200 text-[#0A1128] font-semibold hover:border-[#FF007F] hover:text-[#FF007F] transition">
                  <Camera className="w-4 h-4" /> Rent Studio / Props
                </Link>
                <Link to="/portfolio" data-testid="hero-cta-portfolio"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[#0A1128] font-semibold hover:text-[#FF007F] transition">
                  View Portfolio <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-6 mt-12 max-w-md">
                {[
                  { end: 150, suffix: "+", decimals: 0, l: "Brands launched", delay: 600 },
                  { end: 9.2, suffix: "M", decimals: 1, l: "Reach delivered", delay: 700 },
                  { end: 6.4, suffix: "x", decimals: 1, l: "Avg. ROAS", delay: 800 },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="font-display font-black text-3xl text-gradient">
                      <CountUp end={s.end} suffix={s.suffix} decimals={s.decimals} delay={s.delay} />
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-5 flex items-center justify-center">
              <div className="relative w-full rounded-2xl overflow-hidden border-4 border-white shadow-2xl ring-1 ring-zinc-200 aspect-[4/3]">
                <video
                  src="/cft_home.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[#0A1128] text-white py-6 border-y border-white/10">
        <Marquee gradient={false} speed={50}>
          {["Branding", "Performance Marketing", "Studio Rental", "Reels Production", "E-Commerce",
            "Influencer Marketing", "Photography", "Motion Graphics", "Podcasts"].map((t, i) => (
            <span key={i} className="mx-12 font-display font-black text-3xl uppercase tracking-tighter">
              {t} <span className="text-[#FF007F] mx-6">●</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 lg:py-32 container mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">Services</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              Everything to <span className="text-gradient-animate">grow your brand.</span>
            </h2>
          </div>
          <Link to="/services" className="text-sm font-semibold underline-grow">All services →</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, idx) => {
            const Icon = ICONS[s.icon] || Sparkles;
            const theme = CARD_THEMES[idx % CARD_THEMES.length];
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.07 }}
                whileHover={{ y: -6, boxShadow: "0 12px 36px rgba(0,0,0,0.10)" }}
              >
                <Link to="/services" data-testid={`service-card-${s.id}`}
                  className={`group flex flex-col h-full ${theme.bg} border ${theme.border} rounded-2xl p-7 hover:shadow-lg transition duration-300`}>
                  <div className={`w-12 h-12 rounded-xl ${theme.iconBg} grid place-items-center mb-5 shrink-0`}>
                    <Icon className={`w-6 h-6 ${theme.iconColor}`} />
                  </div>
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-600 mb-1">{s.category}</div>
                  <h3 className="font-display font-bold text-xl mb-2 text-[#0A1128]">{s.title}</h3>
                  <p className="text-sm text-zinc-700 mb-4 leading-relaxed">{s.tagline}</p>
                  <ul className="space-y-1.5 mb-5 flex-1">
                    {s.items.slice(0, 3).map((it) => (
                      <li key={it} className="flex items-center gap-2 text-xs text-zinc-800 font-medium">
                        <span className={`w-1.5 h-1.5 rounded-full ${theme.dot} shrink-0`} />{it}
                      </li>
                    ))}
                    {s.items.length > 3 && (
                      <li className="text-xs text-zinc-600 font-medium pl-3.5">+{s.items.length - 3} more</li>
                    )}
                  </ul>
                  <div className={`flex items-center gap-2 text-sm font-semibold ${theme.link} mt-auto`}>
                    Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* PORTFOLIO STRIP */}
      <section className="py-24 lg:py-32 bg-zinc-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">Recent work</div>
              <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight">
                Stories we've <span className="text-gradient-animate">brought to life.</span>
              </h2>
            </div>
            <Link to="/portfolio" className="text-sm font-semibold underline-grow">View all work →</Link>
          </div>
          {(() => {
            const cols = 3;
            const remainder = portfolio.length % cols;
            const placeholders = remainder === 0 ? 0 : cols - remainder;
            return (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {portfolio.map((p) => (
                  <Link to="/portfolio" key={p.id} data-testid={`portfolio-thumb-${p.id}`}
                    className="group relative overflow-hidden aspect-square rounded-2xl">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
                    <div className="absolute bottom-0 left-0 p-5 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition text-white">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF7F00] mb-1">{p.tags?.[0]}</div>
                      <div className="font-display font-bold text-xl">{p.title}</div>
                    </div>
                  </Link>
                ))}
                {Array.from({ length: placeholders }).map((_, i) => (
                  <div key={`ph-${i}`}
                    className="aspect-square rounded-2xl bg-zinc-100 flex flex-col items-center justify-center gap-3 border-2 border-dashed border-zinc-300">
                    <div className="w-12 h-12 rounded-full bg-zinc-200 flex items-center justify-center">
                      <svg className="w-6 h-6 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 3h18M3 21h18" />
                      </svg>
                    </div>
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-400">Coming Soon</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="py-24 lg:py-32 container mx-auto px-4">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">Growth stories</div>
        <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-12 max-w-4xl">
          Numbers that <span className="text-gradient-animate">speak louder</span> than slogans.
        </h2>
        <div className="grid lg:grid-cols-3 gap-6">
          {cases.map((c) => (
            <Link to={`/case-studies/${c.slug}`} key={c.id} data-testid={`case-card-${c.slug}`}
              className="group bg-white border border-zinc-200 hover:border-[#FF007F] transition rounded-2xl overflow-hidden">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={c.cover} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
              </div>
              <div className="p-6">
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">{c.industry}</div>
                <h3 className="font-display font-bold text-xl mb-4 group-hover:text-[#FF007F] transition">{c.title}</h3>
                <div className="grid grid-cols-3 gap-3 pt-4 border-t border-zinc-200">
                  {c.results.slice(0, 3).map((r) => (
                    <div key={r.metric}>
                      <div className="font-display font-black text-xl text-gradient">{r.value}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{r.metric}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="py-24 lg:py-32 bg-zinc-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">Our Process</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">How we work</h2>
            <p className="text-zinc-500 mt-3 max-w-xl mx-auto">From brief to results — a simple, transparent process that keeps you in control.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ClipboardList, step: "01", title: "Discovery call", desc: "We learn your brand, goals, audience, and budget. No templates — just listening.", color: "#FF007F" },
              { icon: Lightbulb, step: "02", title: "Strategy & brief", desc: "We craft a tailored plan: channels, creatives, timelines, and KPIs you approve.", color: "#FF7F00" },
              { icon: Rocket, step: "03", title: "Execution", desc: "Our senior team builds, shoots, writes, and launches — with weekly check-ins.", color: "#00BFE0" },
              { icon: BarChart3, step: "04", title: "Measure & scale", desc: "Dashboards, reports, and optimizations to compound your results every month.", color: "#9B00FF" },
            ].map((step, idx) => (
              <motion.div key={step.step}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                whileHover={{ y: -6 }}
                className="bg-white rounded-2xl p-7 border border-zinc-200 hover:shadow-lg transition-shadow duration-300 cursor-default">
                <div className="w-12 h-12 rounded-xl grid place-items-center mb-5" style={{ background: step.color + "18" }}>
                  <step.icon className="w-6 h-6" style={{ color: step.color }} />
                </div>
                <div className="text-xs font-mono text-zinc-400 mb-2">{step.step}</div>
                <h4 className="font-display font-bold text-xl mb-2">{step.title}</h4>
                <p className="text-sm text-zinc-500 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="py-16 bg-[#0A1128] text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { end: 150, suffix: "+", decimals: 0, label: "Brands launched", color: "#FF007F", delay: 0 },
              { end: 9.2, suffix: "M", decimals: 1, label: "Reach delivered", color: "#FF7F00", delay: 100 },
              { end: 6.4, suffix: "x", decimals: 1, label: "Avg. ROAS", color: "#00BFE0", delay: 200 },
              { end: 98, suffix: "%", decimals: 0, label: "Client retention", color: "#9B00FF", delay: 300 },
            ].map((stat, idx) => (
              <motion.div key={stat.label}
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}>
                <div className="font-display font-black text-5xl mb-2" style={{ color: stat.color }}>
                  <CountUp end={stat.end} suffix={stat.suffix} decimals={stat.decimals} delay={stat.delay} />
                </div>
                <div className="text-sm text-white/60 uppercase tracking-widest font-mono">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BRANDS MARQUEE */}
      <section className="py-12 border-y border-zinc-100 bg-white">
        <div className="text-center mb-6">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-400">Brands that trust us</p>
        </div>
        <Marquee gradient={false} speed={40}>
          {["Saffron Bistro", "Voltora EV", "Bloom Florals", "Atlas Realty", "Mystic Mocha", "FitNova", "Lumen Watches", "Saree Story", "Spice Route", "Saffron Bistro", "Voltora EV", "Bloom Florals"].map((brand, i) => (
            <div key={i} className="mx-10 flex items-center gap-2 text-zinc-400 font-display font-bold text-lg">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00BFE0]" />
              {brand}
            </div>
          ))}
        </Marquee>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 lg:py-32 bg-[#0A1128] text-white relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="container mx-auto px-4 relative">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#00BFE0] mb-3">Testimonials</div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-12 max-w-3xl">
            Trusted by <span className="text-gradient-animate">founders & marketers</span> who ship.
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {tess.map((t) => (
              <div key={t.id}
                className="bg-white/5 border border-white/10 rounded-2xl p-7 backdrop-blur cursor-default
                  transition-all duration-300
                  hover:bg-white/10 hover:border-[#FF007F]/50
                  hover:-translate-y-1.5 hover:shadow-[0_8px_32px_rgba(255,0,127,0.18)]"
                data-testid={`tess-${t.id}`}>
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-[#FF7F00] text-[#FF7F00]" />)}
                </div>
                <p className="text-lg leading-relaxed mb-6">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <div className="font-bold text-sm">{t.name}</div>
                    <div className="text-xs text-white/60">{t.role}, {t.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/testimonials" className="text-sm font-semibold underline-grow">Read more reviews →</Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 container mx-auto px-4">
        <div className="bg-gradient-brand-animate rounded-3xl p-10 lg:p-16 text-white relative overflow-hidden">
          <div className="absolute inset-0 grain pointer-events-none" />
          {/* Animated blobs */}
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-white/10 animate-blob blur-2xl pointer-events-none" />
          <div className="absolute -bottom-20 left-1/3 w-64 h-64 rounded-full bg-white/10 animate-blob blur-2xl pointer-events-none" style={{animationDelay:"3s"}} />
          <div className="relative max-w-3xl">
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-5">
              Let's build your brand. Together.
            </h2>
            <p className="text-lg opacity-90 mb-8 max-w-xl">
              From a single reel to a full launch — get a no-pressure call with our team.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" data-testid="cta-bottom-consult"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#0A1128] font-bold hover:bg-[#0A1128] hover:text-white transition">
                Book a Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/quote" data-testid="cta-bottom-quote"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-white text-white font-bold hover:bg-white hover:text-[#0A1128] transition">
                <Sparkles className="w-4 h-4" /> Get an AI Quote
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
